import React, { useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Divider,
  List,
  ListItem,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material'
import AccountCircleSharpIcon from '@mui/icons-material/AccountCircleSharp'
import { Message, Add, ErrorOutline } from '@mui/icons-material'
import ChatFilterPanel from './ChatFilterPanel'
import { IFacebookChatRoomCount } from '../../../types/facebook'
import { numberFormat } from '../../../utils'

interface ChatItemProps {
  label?: string
  icon?: React.ReactNode
  count?: number
  isSelected?: boolean
  onClick?: () => void
  isOpenedCustomerPanel?: boolean
}

interface ChatSidebarProps {
  chatRoomsCount?: IFacebookChatRoomCount
  isOpenedCustomerPanel?: boolean
  setParams?: (params: Record<string, any>) => void
}

const ChatItem: React.FC<ChatItemProps> = ({
  label,
  icon,
  count = 0,
  isSelected,
  onClick,
  isOpenedCustomerPanel,
}) => (
  <ListItem disablePadding>
    <Button
      fullWidth
      onClick={onClick}
      sx={{
        justifyContent: !isOpenedCustomerPanel ? 'flex-start' : 'center',
        gap: 1,
        p: 1,
        bgcolor: isSelected ? 'grey.200' : 'transparent',
        height: 48,
        width: !isOpenedCustomerPanel ? '100%' : 'auto',
        margin: !isOpenedCustomerPanel ? '0' : '0 auto',
      }}
    >
      {icon}
      {!isOpenedCustomerPanel && (
        <>
          <Typography variant="body2" color="textSecondary">
            {label}
          </Typography>
          {count > 0 && (
            <Box
              sx={{
                ml: 'auto',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                color: 'white',
              }}
              bgcolor={'error.main'}
            >
              {count}
            </Box>
          )}
        </>
      )}
    </Button>
  </ListItem>
)

const ChatSidebar = (props: ChatSidebarProps) => {
  const { chatRoomsCount, isOpenedCustomerPanel, setParams } = props
  const [status, setStatus] = useState<string | null>('me')

  const handleSetStatus = (status: string) => {
    setStatus(status)
    setParams?.({ chatType: status })
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box p={2} borderBottom={1} borderColor="grey.200">
        <Typography variant="subtitle1" fontWeight="bold">
          กล่องข้อความ
        </Typography>
      </Box>
      <Box p={1.5}>
        <List>
          <ChatItem
            isOpenedCustomerPanel={isOpenedCustomerPanel}
            label={`แชทของคุณ (${numberFormat(
              chatRoomsCount?.assigned?.count ?? 0,
            )})`}
            count={chatRoomsCount?.assigned?.unread ?? 0}
            icon={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'grey.400',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                }}
              >
                <Typography variant="caption" color="white" fontWeight="medium">
                  T
                </Typography>
              </Box>
            }
            onClick={() => handleSetStatus('me')}
            isSelected={status === 'me'}
          />
          <ChatItem
            isOpenedCustomerPanel={isOpenedCustomerPanel}
            label={`ยังไม่ได้มอบหมาย (${numberFormat(
              chatRoomsCount?.unassign?.count ?? 0,
            )})`}
            icon={
              <AccountCircleSharpIcon
                sx={{ fontSize: 28, color: 'grey.400' }}
              />
            }
            count={chatRoomsCount?.unassign?.unread ?? 0}
            onClick={() => handleSetStatus('unassigned')}
            isSelected={status === 'unassigned'}
          />
          <ChatItem
            isOpenedCustomerPanel={isOpenedCustomerPanel}
            label={`แชททั้งหมด (${numberFormat(
              chatRoomsCount?.total?.count ?? 0,
            )})`}
            icon={<Message sx={{ fontSize: 28, color: 'grey.400' }} />}
            count={chatRoomsCount?.total?.unread ?? 0}
            onClick={() => handleSetStatus('all')}
            isSelected={status === 'all'}
          />
        </List>
      </Box>
      <Divider />
    </Box>
  )
}

export default ChatSidebar
